import {
  getAnimalProductToAdd,
  patchAnimalCoverages,
} from "Components/Features/CheckPrice/Products/RiskProducts/Animal/animalUtils";
import { useDispatch, useSelector } from "react-redux";
import { AddProduct, AddProductsToCartData } from "./interfaces";

import { getCoveragePrice } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.helpers";
import { useUpsellFeature } from "Components/Features/Upsell/hooks/useUpsellFeature";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { Species } from "Store/interfaces/products/checkout/riskProducts/AnimalProduct";
import { InsuranceCoverage } from "Store/interfaces/products/coverage";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { PriceFormState } from "Store/priceCalculation/priceCalculationInterfaces";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getProductEditQueryParams } from "Utils/navigation/queryParameterUtils";
import { addProductsToCartStart } from "./slice";

export interface CoverageToAdd {
  coverage: InsuranceCoverage;
  productType: INSURANCE_NAME_VALUE;
  filterValue?: string;
}

export const useAddProductToCartHandler = (
  stepId: string,
): ((formProps: PriceFormState, coveragesToAdd: CoverageToAdd[]) => void) => {
  const isUpsellActive = useUpsellFeature();
  const dispatch = useDispatch();
  const { cartId, productId } = getProductEditQueryParams();
  const { userIdentification, priceCalculation } = useSelector(
    (state: ReduxState) => state,
  );
  const coverages = useCoveragePriceCalculations();

  return (formProps, coveragesToAdd): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: stepId,
        formState: formProps,
      }),
    );

    const products: AddProduct[] = coveragesToAdd.map(
      ({ coverage, filterValue, productType }) => {
        const coveragePrice = getCoveragePrice(
          coverages,
          coverage,
          filterValue,
        );
        return {
          coverage,
          type: productType,
          priceData: coveragePrice,
        };
      },
    );

    const productsData: AddProductsToCartData = {
      isUpsellActive,
      products: products,
      customerData: userIdentification.current,
      formData: {
        ...priceCalculation.priceCalculationFormStates,
        [stepId]: {
          ...priceCalculation.priceCalculationFormStates[stepId],
          ...formProps,
        },
      },
      storedProduct: priceCalculation.storedProduct.current,
      ...(cartId &&
        productId && {
          cartId: cartId,
          productId: productId,
        }),
    };

    dispatch(addProductsToCartStart(productsData));
  };
};

export const useAddAnimalProductToCartHandler = (
  stepId: string,
): ((
  formProps: PriceFormState,
  coveragesToAdd: CoverageToAdd[],
  species: Species,
) => void) => {
  const isUpsellActive = useUpsellFeature();
  const dispatch = useDispatch();
  const { cartId, productId } = getProductEditQueryParams();
  const { userIdentification, priceCalculation } = useSelector(
    (state: ReduxState) => state,
  );

  const coverages = useCoveragePriceCalculations();
  return (formProps, coveragesToAdd, species): void => {
    const patchedCoverages = patchAnimalCoverages(
      coverages,
      formProps.coverage.length === 2,
    );

    dispatch(
      updatePriceCalculationFormState({
        stepId: stepId,
        formState: formProps,
      }),
    );

    const productsData: AddProductsToCartData = {
      isUpsellActive,
      products: [
        getAnimalProductToAdd(coveragesToAdd, patchedCoverages, species),
      ],
      customerData: userIdentification.current,
      formData: {
        ...priceCalculation.priceCalculationFormStates,
        [stepId]: {
          ...priceCalculation.priceCalculationFormStates[stepId],
          ...formProps,
        },
      },
      storedProduct: priceCalculation.storedProduct.current,
      ...(cartId &&
        productId && {
          cartId: cartId,
          productId: productId,
        }),
    };

    dispatch(addProductsToCartStart(productsData));
  };
};
